import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { Trophy, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 p-3 animate-pulse">
      <div className="w-8 h-8 rounded-full bg-[#F0E6D8]" />
      <div className="flex-1 space-y-2">
        <div className="h-3 bg-[#F0E6D8] rounded w-24" />
        <div className="h-2 bg-[#F0E6D8] rounded w-16" />
      </div>
      <div className="h-5 bg-[#F0E6D8] rounded w-10" />
    </div>
  );
}

export default function LeaderboardPage() {
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/leaderboard`, {
          headers: { Authorization: `Bearer ${token}` }, withCredentials: true
        });
        setLeaders(res.data.leaders || []);
      } catch { /* ignore */ } finally { setLoading(false); }
    };
    fetch();
  }, [API, token]);

  const medals = ['🥇', '🥈', '🥉'];
  const bgColors = ['#FFD23F', '#E5DACE', '#F5B895'];

  return (
    <div className="min-h-screen pb-safe" style={{ background: '#FFF8F0' }}>
      <div className="px-4 pt-4 pb-2 flex items-center gap-3">
        <button onClick={() => navigate('/home')}><ArrowLeft size={22} color="#1B4F8A" /></button>
        <h1 className="text-2xl font-extrabold" style={{ color: '#1B4F8A' }}>Leaderboard</h1>
        <Trophy size={22} color="#FFD23F" />
      </div>

      <div className="px-4">
        <p className="text-xs font-semibold mb-4" style={{ color: '#8B7355' }}>Top 10 streakers this week</p>

        {/* Top 3 Podium */}
        {!loading && leaders.length >= 3 && (
          <div className="flex items-end justify-center gap-3 mb-6" data-testid="podium">
            {[1, 0, 2].map((idx) => {
              const l = leaders[idx];
              if (!l) return null;
              const height = idx === 0 ? 120 : idx === 1 ? 100 : 80;
              return (
                <div key={idx} className="flex flex-col items-center" style={{ width: '30%' }}>
                  <span className="text-2xl mb-1">{medals[idx]}</span>
                  <p className="text-xs font-extrabold truncate w-full text-center" style={{ color: '#1B4F8A' }}>{l.name}</p>
                  <p className="text-[10px] font-semibold" style={{ color: '#8B7355' }}>Class {l.class_level}</p>
                  <div className="w-full rounded-t-2xl flex items-end justify-center mt-2"
                    style={{ height, background: bgColors[idx] + '40', borderTop: `3px solid ${bgColors[idx]}` }}>
                    <p className="text-lg font-black pb-2" style={{ color: '#FF6B35' }}>{l.current_streak}🔥</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Full List */}
        <div className="bg-white border-2 rounded-3xl overflow-hidden" style={{ borderColor: '#E5DACE' }} data-testid="leaderboard-list">
          {loading ? (
            Array(5).fill(0).map((_, i) => <SkeletonRow key={i} />)
          ) : leaders.length === 0 ? (
            <div className="text-center py-8">
              <span className="text-4xl block mb-2">🏆</span>
              <p className="text-sm font-semibold" style={{ color: '#8B7355' }}>No data yet. Start practicing to appear here!</p>
            </div>
          ) : (
            leaders.map((l, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 border-b last:border-0"
                style={{ borderColor: '#F0E6D8', background: i < 3 ? bgColors[i] + '10' : 'transparent' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center font-black text-sm"
                  style={{ background: i < 3 ? bgColors[i] + '40' : '#F0E6D8', color: '#1B4F8A' }}>
                  {i < 3 ? medals[i] : i + 1}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold" style={{ color: '#1B4F8A' }}>{l.name}</p>
                  <p className="text-xs font-semibold" style={{ color: '#8B7355' }}>Class {l.class_level} · {l.total_days} days practiced</p>
                </div>
                <p className="text-sm font-black" style={{ color: '#FF6B35' }}>{l.current_streak}🔥</p>
              </div>
            ))
          )}
        </div>
      </div>

      <BottomNav active="home" />
    </div>
  );
}
